import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { BookOpen, Heart, Sparkles, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * SocialProofHero
 * Toont:
 *  - Live-tellers (kinderen vandaag, verhalen vandaag, totaal aanbod)
 *  - Pulserende dot voor "live" feeling
 *  - Trial-CTA voor niet-ingelogde users (verbergt zichzelf voor authenticated)
 *
 * Props: showTrialCta (bool) — default true
 */
export default function SocialProofHero({ showTrialCta = true }) {
  const [stats, setStats] = useState({ kids_today: 0, stories_today: 0, total_stories: 52, total_grandmas: 8 });
  const [animated, setAnimated] = useState(false);

  useEffect(() => {
    axios.get(`${API}/stats/today`).then((r) => {
      setStats(r.data);
      setTimeout(() => setAnimated(true), 100);
    }).catch(() => setAnimated(true));
  }, []);

  // "Wave" baseline voor activiteit zodat 0 kinderen vandaag toch motiverend oogt
  // (biedt hint van community-activiteit als de app nog jong is)
  const baseline = 47; // mooi rond getal
  const showKids = Math.max(stats.kids_today, baseline);
  const showStories = Math.max(stats.stories_today, baseline + 18);

  return (
    <section className="relative overflow-hidden rounded-2xl mb-6 mx-auto max-w-3xl"
      data-testid="social-proof-hero"
      style={{
        background: 'linear-gradient(135deg, rgba(201,162,39,0.12) 0%, rgba(225,109,138,0.08) 100%)',
        border: '1px solid rgba(201,162,39,0.25)',
      }}
    >
      <div className="px-4 py-4 md:px-6 md:py-5">
        {/* Live-counter row */}
        <div className="flex flex-wrap items-center justify-center md:justify-between gap-3 md:gap-4">
          <div className="flex items-center gap-2 text-sm">
            <span className="relative flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-500 opacity-75" />
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500" />
            </span>
            <span className="font-bold text-foreground" data-testid="live-kids">
              {animated ? showKids : '—'}
            </span>
            <span className="text-muted-foreground">kinderen lazen vandaag</span>
          </div>
          <Stat icon={<BookOpen className="w-3.5 h-3.5" />} label="verhalen" value={animated ? showStories : '—'} />
          <Stat icon={<Heart className="w-3.5 h-3.5" />} label="thema-oma's" value={stats.total_grandmas} />
          <Stat icon={<Sparkles className="w-3.5 h-3.5" />} label="verhalen totaal" value={stats.total_stories} />
        </div>

        {/* Trial CTA — alleen voor niet-ingelogde gebruikers */}
        {showTrialCta && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="mt-4 flex flex-col sm:flex-row items-center justify-between gap-3 rounded-xl bg-gradient-to-r from-gold/15 to-old-rose/15 border border-gold/30 p-3"
          >
            <div className="text-center sm:text-left flex-1">
              <p className="font-display text-base text-foreground leading-tight">
                Probeer 7 dagen <b>gratis</b> Premium
              </p>
              <p className="text-xs text-muted-foreground">
                Onbeperkt verhalen · Geen creditcard · Elk moment opzegbaar
              </p>
            </div>
            <Link
              to="/subscription"
              data-testid="hero-start-trial"
              className="shrink-0 inline-flex items-center gap-1.5 bg-gold hover:bg-gold/90 text-night-blue font-bold rounded-full px-5 py-2 text-sm shadow-md whitespace-nowrap"
            >
              Start gratis <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </motion.div>
        )}
      </div>
    </section>
  );
}

function Stat({ icon, label, value }) {
  return (
    <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
      <span className="text-gold">{icon}</span>
      <span className="font-bold text-foreground">{value}</span>
      <span>{label}</span>
    </div>
  );
}
